import { useState } from 'react';
import { Bell, X, ChefHat, Check } from 'lucide-react';
import { useNotificationStore, Notification } from '../stores/notificationStore';
import clsx from 'clsx';
import { formatDistanceToNowStrict } from 'date-fns';

export default function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const { notifications, unreadCount, markAsRead, markAllAsRead, dismissNotification } = useNotificationStore();

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen && unreadCount > 0) {
      // Mark all as read when opening
      markAllAsRead();
    }
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'course_ready':
        return <ChefHat className="w-5 h-5 text-emerald-400" />;
      case 'success':
        return <Check className="w-5 h-5 text-emerald-400" />;
      default:
        return <Bell className="w-5 h-5 text-blue-400" />;
    }
  };

  return (
    <div className="relative">
      <button
        onClick={handleToggle}
        className={clsx(
          'relative p-3 rounded-xl transition-colors',
          unreadCount > 0
            ? 'bg-emerald-600 text-white animate-pulse'
            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
        )}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs font-bold flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className="fixed sm:absolute left-3 right-3 sm:left-auto sm:right-0 top-[calc(env(safe-area-inset-top)+4rem)] sm:top-full sm:mt-2 sm:w-80 max-h-[70dvh] overflow-auto overscroll-contain bg-slate-800 rounded-xl shadow-xl border border-slate-700 z-50" style={{ WebkitOverflowScrolling: "touch" }}>
            <div className="sticky top-0 bg-slate-800 border-b border-slate-700 px-4 py-3 flex items-center justify-between">
              <h3 className="font-semibold text-white">Notifications</h3>
              {notifications.length > 0 && (
                <button
                  onClick={() => {
                    notifications.forEach((n) => dismissNotification(n.id));
                  }}
                  className="text-xs text-slate-400 hover:text-white"
                >
                  Clear all
                </button>
              )}
            </div>

            {notifications.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-700">
                {notifications.slice(0, 20).map((notification) => (
                  <div
                    key={notification.id}
                    className={clsx(
                      'p-4 hover:bg-slate-700/50 transition-colors',
                      !notification.read && 'bg-slate-700/30'
                    )}
                  >
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-medium text-white text-sm">
                            {notification.title}
                          </p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              dismissNotification(notification.id);
                            }}
                            className="p-1 hover:bg-slate-600 rounded"
                          >
                            <X className="w-3 h-3 text-slate-400" />
                          </button>
                        </div>
                        <p className="text-sm text-slate-400 mt-0.5">
                          {notification.message}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {formatDistanceToNowStrict(notification.timestamp, { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
